<script setup lang="ts">

import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { DsButton, DsIcon, DsSkeleton } from '@demvsystems/design-components';
import FileInput from '@/upload/components/FileInput.vue';
import InvalidFilesHint from '@/upload/components/InvalidFilesHint.vue';
import AiInfo from '@/upload/components/AiInfo.vue';
import {
  allowedContentTypes,
  DOCUMENT_EXTRACTION_STEP,
  useAiDocumentExtractionStore
} from '@/documents/store/aiDocumentExtractionStore.ts';
import AiUploadTypesHint from '@/upload/components/AiUploadTypesHint.vue';
import { ElButton, ElTooltip } from 'element-plus';

const emit = defineEmits(['close'])

const filesCount = ref<number|null>(null);
const aiDocumentExtractionStore = useAiDocumentExtractionStore();
const {
  step,
  uploadedFiles,
  invalidFiles,
  errorMessages,
  extractError,
  hasExtract,
} = storeToRefs(aiDocumentExtractionStore);

const loadingCount = computed(() => {
  if (filesCount.value === null) return 0;
  return filesCount.value - uploadedFiles.value.size;
})

async function handleExtract() {
  await aiDocumentExtractionStore.extractDocuments();
  if (extractError.value) return
  emit('close');
}

function handleClassicCreateContract() {
  if (!hasExtract.value) aiDocumentExtractionStore.$reset();
  emit('close');
}

function handleInput(files: FileList) {
  aiDocumentExtractionStore.uploadFiles(files);
  filesCount.value = files.length;
}
</script>

<template>
  <div class="flex flex-col gap-7 h-full">
    <AiInfo>
      <DsButton
        size="sm"
        variant="clear"
        class="mr-auto -ml-3"
        @click="handleClassicCreateContract"
      >
        {{ hasExtract ? 'Zum Ergebnis' : 'Zur manuellen Vertragsanlage ohne KI' }}
      </DsButton>
    </AiInfo>

    <div class="flex flex-col gap-1">
      <FileInput
        :accept="allowedContentTypes.join(', ')"
        @input="handleInput"
      >
        <AiUploadTypesHint />
      </FileInput>

      <InvalidFilesHint
        :files="invalidFiles"
        :error-messages="errorMessages"
      />

      <div v-if="uploadedFiles.size > 0" class="flex flex-col gap-1 max-h-32 overflow-y-auto">
        <div
          v-for="[upload, name] in uploadedFiles"
          :key="upload"
          class="flex grow items-center justify-between gap-2"
        >
          <ElTooltip :content="name" placement="top">
            <span class="text-gray-700 text-sm truncate">{{ name }}</span>
          </ElTooltip>
          <ElButton
            circle
            size="small"
            @click="aiDocumentExtractionStore.removeUpload(upload)"
          >
            <template #icon>
              <DsIcon name="times" />
            </template>
          </ElButton>
        </div>
      </div>

      <div v-if="step === DOCUMENT_EXTRACTION_STEP.UPLOAD">
        <DsSkeleton
          v-for="count in loadingCount"
          :key="count"
          class="h-5 rounded-lg w-full mt-2"
        />
      </div>
    </div>

    <div v-if="extractError">
      {{ extractError }}
    </div>

    <DsButton
      v-if="uploadedFiles.size > 0"
      size="lg"
      class="w-full mt-auto sticky bottom-0"
      :handler="handleExtract"
    >
      {{ uploadedFiles.size === 1 ? 'Dokument' : 'Dokumente' }} verarbeiten
    </DsButton>
  </div>
</template>
